import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from '../utils/axiosInstance';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { BASE_URL } from '@/config/constants';
import { toast } from 'sonner';
import { Music, Play, LinkIcon, Headphones, Wrench, RefreshCw, Instagram } from 'lucide-react';
import { PageLoader } from '@/components/ui/page-loader';
import { Pagination } from '@/components/ui/pagination';
import { usePagination } from '@/hooks/usePagination';
import { Dropdown } from '@/components/ui/dropdown';
import { usePlan } from '@/context/PlanContext';
import { PlanLimitBadge, GracePeriodBanner, PlanItemBadge } from '@/components/PlanComponents';
import SectionTextSettingsCard from '@/components/SectionTextSettingsCard';
import ImageUploader from '../components/ImageUploader';

import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Input } from '@/components/ui/input';

interface Music {
  _id: string;
  title: string;
  category: string;
  youtube: string;
  soundcloudUrl?: string;
  spotifyUrl?: string;
  instagramUrl?: string;
  thumbnail?: string;
  order: number;
  isActive?: boolean;
  metadata?: {
    views?: number;
    releaseDate?: string;
  };
  createdAt?: string;
}

type SortMode = 'custom' | 'views' | 'latest';

interface SortableTableRowProps {
  item: Music;
  index: number;
  categories: Category[];
  onEdit: (item: Music) => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string) => void;
  onCategoryChange: (id: string, categoryId: string) => void;
  togglingId: string | null;
}

const SortableTableRow: React.FC<SortableTableRowProps> = ({ item, index, categories, onEdit, onDelete, onToggleActive, onCategoryChange, togglingId }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item._id });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    backgroundColor: isDragging ? 'rgba(0, 0, 0, 0.05)' : undefined,
  };

  return (
    <TableRow ref={setNodeRef} style={style} className="hover:bg-muted/50 transition-colors">
      <TableCell className="w-10 select-none cursor-grab">
        <div className="flex items-center gap-2" {...attributes} {...listeners}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground">
            <circle cx="9" cy="12" r="1"/><circle cx="9" cy="5" r="1"/><circle cx="9" cy="19" r="1"/>
            <circle cx="15" cy="12" r="1"/><circle cx="15" cy="5" r="1"/><circle cx="15" cy="19" r="1"/>
          </svg>
          <span className="w-4 text-muted-foreground font-medium">{index + 1}</span>
        </div>
      </TableCell>
      <TableCell className="font-medium text-foreground max-w-50">
        <div className="wrap-break-word whitespace-normal">
          {item.title}
        </div>
      </TableCell>
      <TableCell>
        <div className="min-w-[120px]">
          <Dropdown
            value={item.category}
            options={[
              { value: '', label: 'Select Category' },
              ...categories.map(c => ({
                value: c._id,
                label: c.parentCategory ? `${c.parentCategory.name} › ${c.name}` : c.name,
              })),
            ]}
            onValueChange={(val) => {
              if (val) onCategoryChange(item._id, val as string);
            }}
            triggerClassName="!w-auto !py-1 !px-2 !text-xs"
            panelClassName="!min-w-[180px]"
          />
        </div>
      </TableCell>
      <TableCell>
        {item.instagramUrl ? (
          <a href={item.instagramUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline flex items-center gap-1">
            <Instagram className="w-3.5 h-3.5 shrink-0" />
            Instagram
          </a>
        ) : item.spotifyUrl ? (
          <a href={item.spotifyUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline flex items-center gap-1">
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 shrink-0" fill="currentColor">
              <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
            </svg>
            Spotify
          </a>
        ) : item.soundcloudUrl ? (
          <a href={item.soundcloudUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline flex items-center gap-1">
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 shrink-0" fill="currentColor">
              <path d="M1.175 12.225c-.015 0-.024.01-.024.023l-.272 2.308.272 2.316c0 .014.01.023.024.023.013 0 .023-.01.023-.023l.308-2.316-.308-2.308c0-.013-.01-.023-.023-.023zm.89-.899c-.02 0-.032.013-.032.03l-.311 3.206.31 3.234c0 .018.013.031.032.031.018 0 .031-.013.031-.03l.353-3.235-.353-3.206c0-.017-.013-.03-.031-.03zm.919-.31c-.023 0-.04.017-.04.039l-.295 3.516.295 3.544c0 .022.017.039.04.039.022 0 .04-.017.04-.039l.335-3.544-.335-3.516c0-.022-.018-.039-.04-.039zm.927-.164c-.027 0-.048.021-.048.047l-.277 3.68.277 3.705c0 .026.021.047.048.047.026 0 .047-.021.047-.047l.316-3.705-.316-3.68c0-.026-.021-.047-.047-.047zm.934-.11c-.03 0-.054.024-.054.054l-.26 3.79.26 3.811c0 .03.024.054.054.054.03 0 .054-.024.054-.054l.295-3.811-.295-3.79c0-.03-.024-.054-.054-.054zm.944-.082c-.033 0-.06.027-.06.061l-.244 3.872.244 3.89c0 .033.027.06.06.06.034 0 .06-.027.06-.06l.278-3.89-.278-3.872c0-.034-.026-.061-.06-.061zm.955-.055c-.037 0-.067.03-.067.067l-.228 3.927.228 3.942c0 .037.03.067.067.067.037 0 .067-.03.067-.067l.26-3.942-.26-3.927c0-.037-.03-.067-.067-.067zm.964-.04c-.04 0-.073.033-.073.073l-.212 3.967.212 3.978c0 .04.033.073.073.073.04 0 .073-.033.073-.073l.24-3.978-.24-3.967c0-.04-.033-.073-.073-.073zm.974-.027c-.044 0-.08.036-.08.08l-.195 3.994.195 4.002c0 .044.036.08.08.08.044 0 .08-.036.08-.08l.221-4.002-.221-3.994c0-.044-.036-.08-.08-.08zm.985-.014c-.047 0-.086.039-.086.086l-.179 4.008.179 4.015c0 .047.039.086.086.086.047 0 .086-.039.086-.086l.202-4.015-.202-4.008c0-.047-.039-.086-.086-.086zm.994-.003c-.05 0-.092.042-.092.092l-.162 4.011.162 4.017c0 .05.042.092.092.092.05 0 .092-.042.092-.092l.184-4.017-.184-4.011c0-.05-.042-.092-.092-.092zm1.003.01c-.054 0-.098.044-.098.098l-.146 4.001.146 4.006c0 .054.044.098.098.098.054 0 .098-.044.098-.098l.165-4.006-.165-4.001c0-.054-.044-.098-.098-.098zm1.012.023c-.057 0-.104.047-.104.104l-.13 3.978.13 3.981c0 .057.047.104.104.104.057 0 .104-.047.104-.104l.147-3.981-.147-3.978c0-.057-.047-.104-.104-.104zm1.021.037c-.06 0-.11.05-.11.11l-.113 3.941.113 3.943c0 .06.05.11.11.11.06 0 .11-.05.11-.11l.128-3.943-.128-3.941c0-.06-.05-.11-.11-.11zm1.03.05c-.063 0-.114.051-.114.114l-.097 3.891.097 3.892c0 .063.051.114.114.114.063 0 .114-.051.114-.114l.11-3.892-.11-3.891c0-.063-.051-.114-.114-.114zm1.039.063c-.066 0-.12.054-.12.12l-.08 3.828.08 3.829c0 .066.054.12.12.12.066 0 .12-.054.12-.12l.091-3.829-.091-3.828c0-.066-.054-.12-.12-.12zm1.048.077c-.069 0-.125.056-.125.125l-.064 3.751.064 3.751c0 .069.056.125.125.125.069 0 .125-.056.125-.125l.072-3.751-.072-3.751c0-.069-.056-.125-.125-.125zm1.057.09c-.072 0-.13.058-.13.13l-.047 3.661.047 3.66c0 .072.058.13.13.13.072 0 .13-.058.13-.13l.053-3.66-.053-3.661c0-.072-.058-.13-.13-.13zm2.186-.545c-.282 0-.548.06-.787.167-.163-1.853-1.72-3.295-3.617-3.295-.496 0-.967.104-1.39.291-.159.066-.201.133-.203.194v6.477c.002.063.051.113.114.118h5.883c.483 0 .875-.392.875-.875v-2.202c0-.483-.392-.875-.875-.875z"/>
            </svg>
            SoundCloud
          </a>
        ) : (
          <a href={item.youtube} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline flex items-center gap-1">
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 shrink-0" fill="currentColor">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
            YouTube
          </a>
        )}
      </TableCell>
      <TableCell>
        {item.thumbnail ? (
          <img
            src={item.thumbnail}
            alt="thumbnail"
            className="w-16 h-16 object-cover rounded"
            onError={(e) => {
              const vid = item.youtube ? extractYouTubeVideoId(item.youtube) : null;
              if (vid) {
                const fallback = `https://img.youtube.com/vi/${vid}/hqdefault.jpg`;
                if ((e.target as HTMLImageElement).src !== fallback) {
                  (e.target as HTMLImageElement).src = fallback;
                }
              }
            }}
          />
        ) : item.youtube ? (
          <img
            src={`https://img.youtube.com/vi/${extractYouTubeVideoId(item.youtube)}/hqdefault.jpg`}
            alt="thumbnail"
            className="w-16 h-16 object-cover rounded"
          />
        ) : (
          <div className="w-16 h-16 rounded bg-muted flex items-center justify-center">
            <Music className="w-6 h-6 text-muted-foreground" />
          </div>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center space-x-2">
          <Switch
            checked={item.isActive || false}
            onCheckedChange={() => onToggleActive(item._id)}
            disabled={togglingId === item._id}
            aria-label="Toggle showcase active state"
          />
          <span className={item.isActive ? 'status-label-active' : 'status-label-inactive'}>
            {item.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
      </TableCell>
      <TableCell>
        <PlanItemBadge resourceType="showcases" index={index} />
      </TableCell>
      <TableCell>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="icon-sm"
            onClick={() => onEdit(item)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
          </Button>
          <Button
            variant="destructive"
            size="icon-sm"
            onClick={() => onDelete(item._id)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

interface Category {
  _id: string;
  name: string;
  parentCategory?: {
    _id: string;
    name: string;
  } | null;
}

const emptyMusic: MusicForm = {
  title: '',
  category: '',
  youtube: '',
  soundcloudUrl: '',
  spotifyUrl: '',
  instagramUrl: '',
  thumbnail: '',
  order: 0,
};

type MusicForm = {
  title: string;
  category: string;
  youtube: string;
  soundcloudUrl: string;
  spotifyUrl: string;
  instagramUrl: string;
  thumbnail: string;
  order: number;
};
const extractYouTubeVideoId = (url: string): string | null => {
  // YouTube Shorts (/shorts/<id>) carry the id in the path, not a v= param.
  const shortsMatch = url.match(/youtube\.com\/shorts\/([^&\n?#/]+)/);
  if (shortsMatch) return shortsMatch[1];
  const regex = /(?:youtube\.com\/.*[?&]v=|youtu\.be\/)([^&\n?#]+)/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

const extractSpotifyTrackId = (url: string): string | null => {
  const match = url.match(/spotify\.com\/track\/([a-zA-Z0-9]+)/);
  return match ? match[1] : null;
};

type InstagramParse =
  | { ok: true; type: 'reel' | 'post'; shortcode: string; canonicalUrl: string }
  | { ok: false; code: 'invalid' | 'unsupported'; message: string };

// Parse a pasted Instagram URL into its normalized parts. Supports Reels
// (/reel/) and Posts (/p/). Rejects Stories, Highlights, profiles, IGTV (/tv/).
const parseInstagramInput = (raw: string): InstagramParse => {
  if (!raw || !raw.trim()) return { ok: false, code: 'invalid', message: 'Instagram URL is required' };
  let candidate = raw.trim();
  if (!/^https?:\/\//i.test(candidate)) candidate = `https://${candidate}`;
  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return { ok: false, code: 'invalid', message: 'Invalid Instagram URL' };
  }
  const host = parsed.hostname.toLowerCase();
  const isIg = host === 'instagram.com' || host === 'www.instagram.com' || host === 'instagr.am' || host === 'www.instagr.am' || host.endsWith('.instagram.com');
  if (!isIg) return { ok: false, code: 'invalid', message: 'Invalid Instagram URL' };

  const parts = parsed.pathname.split('/').filter(Boolean);
  // Accept both canonical (/reel/{code}, /p/{code}) and username-prefixed
  // (/{username}/reel/{code}, /{username}/p/{code}) forms.
  const kindIdx = parts[0] === 'reel' || parts[0] === 'p' ? 0 : parts[1] === 'reel' || parts[1] === 'p' ? 1 : -1;
  const kind = kindIdx >= 0 ? parts[kindIdx].toLowerCase() : '';
  if (kind === 'reel' || kind === 'p') {
    const shortcode = parts[kindIdx + 1];
    if (shortcode && /^[A-Za-z0-9_-]+$/.test(shortcode)) {
      const path = kind === 'reel' ? 'reel' : 'p';
      return {
        ok: true,
        type: kind === 'reel' ? 'reel' : 'post',
        shortcode,
        canonicalUrl: `https://www.instagram.com/${path}/${shortcode}/`,
      };
    }
    return { ok: false, code: 'invalid', message: 'Invalid Instagram URL. Please paste a direct Post or Reel link.' };
  }
  return {
    ok: false,
    code: 'unsupported',
    message: 'Unsupported Instagram URL. Only Posts (instagram.com/p/...) and Reels (instagram.com/reel/...) are supported.',
  };
};

const extractInstagramShortcode = (url: string): { type: 'reel' | 'post'; shortcode: string } | null => {
  const p = parseInstagramInput(url);
  return p.ok ? { type: p.type, shortcode: p.shortcode } : null;
};

interface ShowcaseManagerProps {
  domain: string;
}

const ShowcaseManager: React.FC<ShowcaseManagerProps> = ({ domain }) => {
  const { canAdd, openUpgradeModal, adjustUsage, isSuperAdmin } = usePlan();
  const location = useLocation();
  const navigate = useNavigate();
  const [music, setMusic] = useState<Music[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [form, setForm] = useState<MusicForm>(emptyMusic);
  const [editId, setEditId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [selectedParent, setSelectedParent] = useState('');

  const [mediaType, setMediaType] = useState<'youtube' | 'soundcloud' | 'spotify' | 'instagram'>('youtube');
  const [urlInput, setUrlInput] = useState('');
  const [urlError, setUrlError] = useState('');
  const [sortMode, setSortMode] = useState<SortMode>('custom');

  // Rebuild state (super-admin only)
  const [rebuildOpen, setRebuildOpen] = useState(false);
  const [rebuildInput, setRebuildInput] = useState('');
  const [rebuildPreview, setRebuildPreview] = useState<any>(null);
  const [rebuildLoading, setRebuildLoading] = useState(false);
  const [rebuildConfirming, setRebuildConfirming] = useState(false);
  const [rebuildError, setRebuildError] = useState('');


  const fetchMusic = () => {
    setLoading(true);
    axios.get(BASE_URL + '/api/showcase')
      .then(res => setMusic(res.data))
      .finally(() => setLoading(false));
  };

  const fetchCategories = () => {
    axios.get(BASE_URL + '/api/category', { params: { type: 'showcase' } })
      .then(res => setCategories(res.data));
  };

  useEffect(() => {
    fetchMusic();
    fetchCategories();
  }, [domain]);

  // Auto-open the "Add Showcase" dialog when navigated here from the dashboard
  // card (state.openAdd). Mirrors the Add button's plan-limit behaviour, then
  // clears the navigation state so it doesn't re-trigger on refresh/back.
  useEffect(() => {
    if (!(location.state as { openAdd?: boolean } | null)?.openAdd) return;
    navigate(location.pathname, { replace: true, state: null });
    if (!canAdd('showcases')) {
      openUpgradeModal('showcases');
    } else {
      setMediaType('youtube');
      setUrlInput('');
      setForm(emptyMusic);
      setSelectedParent('');
      setAddOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Pagination
  const { startIndex, paginate, paginationProps } = usePagination(music.length);
  const paginatedMusic = paginate(music);

  const parentCategories = categories.filter(cat => !cat.parentCategory);
  const subcategories = categories.filter(cat => cat.parentCategory && cat.parentCategory._id === selectedParent);
  const hasSubcategories = subcategories.length > 0;

  const fetchYouTubeVideoDetails = async (videoId: string) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/youtube/video-details/${videoId}`);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch video details:', err);
      return null;
    }
  };

  const fetchSoundCloudDetails = async (url: string) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/soundcloud/track-details`, { params: { url } });
      return response.data;
    } catch (err) {
      console.error('Failed to fetch SoundCloud details:', err);
      return null;
    }
  };

  const fetchSpotifyDetails = async (url: string) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/spotify/track-details`, { params: { url } });
      return response.data;
    } catch (err) {
      console.error('Failed to fetch Spotify details:', err);
      return null;
    }
  };

  const fetchInstagramDetails = async (url: string) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/instagram/reel-details`, { params: { url } });
      return response.data;
    } catch (err) {
      console.error('Failed to fetch Instagram details:', err);
      return null;
    }
  };

  const handleFormChange = async (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const target = e.target as HTMLInputElement;
    const { name, value, type, checked } = target;
    const newValue = type === 'checkbox' ? checked : value;

    const updatedForm = { ...form, [name]: newValue };

    if (name === 'youtube' && typeof newValue === 'string') {
      const videoId = extractYouTubeVideoId(newValue);
      if (videoId) {
        updatedForm.thumbnail = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
        setForm(updatedForm);

        // Fetch video details
        setIsFetching(true);
        try {
          const videoDetails = await fetchYouTubeVideoDetails(videoId);
          if (videoDetails?.title) {
            setForm(prev => ({ ...prev, title: videoDetails.title }));
          }
        } finally {
          setIsFetching(false);
        }
        return;
      }
    }

    if (name === 'soundcloudUrl' && typeof newValue === 'string' && newValue.includes('soundcloud.com/')) {
      setForm(updatedForm);
      setIsFetching(true);
      try {
        const details = await fetchSoundCloudDetails(newValue);
        if (details) {
          setForm(prev => ({
            ...prev,
            title: details.title || prev.title,
            thumbnail: details.thumbnail || '',
          }));
        }
      } finally {
        setIsFetching(false);
      }
      return;
    }

    if (name === 'spotifyUrl' && typeof newValue === 'string' && newValue.includes('spotify.com/track/')) {
      setForm(updatedForm);
      setIsFetching(true);
      try {
        const details = await fetchSpotifyDetails(newValue);
        if (details) {
          setForm(prev => ({
            ...prev,
            title: details.title || prev.title,
            thumbnail: details.thumbnail || '',
          }));
        }
      } finally {
        setIsFetching(false);
      }
      return;
    }

    setForm(updatedForm);
  };

  const handleUrlInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUrlInput(value);

    if (!value) {
      setUrlError('');
      setForm(prev => ({ ...prev, youtube: '', soundcloudUrl: '', spotifyUrl: '', instagramUrl: '', thumbnail: '', title: '' }));
      return;
    }

    const ytId = extractYouTubeVideoId(value);
    if (ytId) {
      setUrlError('');
      setMediaType('youtube');
      setForm(prev => ({ ...prev, youtube: value, soundcloudUrl: '', spotifyUrl: '', instagramUrl: '', thumbnail: `https://img.youtube.com/vi/${ytId}/maxresdefault.jpg` }));
      setIsFetching(true);
      try {
        const videoDetails = await fetchYouTubeVideoDetails(ytId);
        if (videoDetails?.title) {
          setForm(prev => ({ ...prev, title: videoDetails.title }));
        }
      } finally {
        setIsFetching(false);
      }
      return;
    }

    const ig = parseInstagramInput(value);
    if (ig.ok) {
      setUrlError('');
      setMediaType('instagram');
      const sc = extractInstagramShortcode(value);
      const canonicalUrl = sc
        ? `https://www.instagram.com/${sc.type === 'reel' ? 'reel' : 'p'}/${sc.shortcode}/`
        : ig.canonicalUrl;
      setForm(prev => ({ ...prev, instagramUrl: canonicalUrl, youtube: '', soundcloudUrl: '', spotifyUrl: '' }));
      setIsFetching(true);
      try {
        const details = await fetchInstagramDetails(canonicalUrl);
        if (details) {
          setForm(prev => ({ ...prev, title: details.title || prev.title, thumbnail: details.thumbnail || '' }));
        }
      } finally {
        setIsFetching(false);
      }
      return;
    }

    if (value.includes('soundcloud.com/')) {
      setUrlError('');
      setMediaType('soundcloud');
      setForm(prev => ({ ...prev, soundcloudUrl: value, youtube: '', spotifyUrl: '', instagramUrl: '' }));
      setIsFetching(true);
      try {
        const details = await fetchSoundCloudDetails(value);
        if (details) {
          setForm(prev => ({ ...prev, title: details.title || prev.title, thumbnail: details.thumbnail || '' }));
        }
      } finally {
        setIsFetching(false);
      }
      return;
    }

    if (value.includes('spotify.com/track/')) {
      setUrlError('');
      setMediaType('spotify');
      setForm(prev => ({ ...prev, spotifyUrl: value, youtube: '', soundcloudUrl: '', instagramUrl: '' }));
      setIsFetching(true);
      try {
        const details = await fetchSpotifyDetails(value);
        if (details) {
          setForm(prev => ({ ...prev, title: details.title || prev.title, thumbnail: details.thumbnail || '' }));
        }
      } finally {
        setIsFetching(false);
      }
      return;
    }

    // Platform detected but wrong URL format
    if (value.includes('instagram.com') || value.includes('instagr.am')) {
      setUrlError(ig.message);
      setForm(prev => ({ ...prev, youtube: '', soundcloudUrl: '', spotifyUrl: '', instagramUrl: '', thumbnail: '', title: '' }));
      return;
    }

    if (value.includes('youtube.com') || value.includes('youtu.be')) {
      setUrlError('YouTube link detected. Please paste a direct video or Shorts link (e.g. youtube.com/watch?v=..., youtu.be/..., or youtube.com/shorts/...).');
      setForm(prev => ({ ...prev, youtube: '', soundcloudUrl: '', spotifyUrl: '', instagramUrl: '', thumbnail: '', title: '' }));
      return;
    }

    if (value.includes('spotify.com')) {
      setUrlError('Spotify link detected. Please paste a track link (e.g. open.spotify.com/track/...).');
      setForm(prev => ({ ...prev, youtube: '', soundcloudUrl: '', spotifyUrl: '', instagramUrl: '', thumbnail: '', title: '' }));
      return;
    }

    if (value.includes('soundcloud.com')) {
      setUrlError('SoundCloud link detected. Please paste a direct track link (e.g. soundcloud.com/artist/track-name).');
      setForm(prev => ({ ...prev, youtube: '', soundcloudUrl: '', spotifyUrl: '', instagramUrl: '', thumbnail: '', title: '' }));
      return;
    }

    // URL not recognized at all
    setUrlError('Please paste a valid YouTube, Spotify, SoundCloud, or Instagram link.');
    setForm(prev => ({ ...prev, youtube: '', soundcloudUrl: '', spotifyUrl: '', instagramUrl: '', thumbnail: '', title: '' }));
  };

  const handleParentSelect = (parentId: string) => {
    setSelectedParent(parentId);

    const hasSub = categories.some(cat => cat.parentCategory?._id === parentId);

    setForm(prev => ({
      ...prev,
      category: hasSub ? '' : parentId,
    }));
  };

  const handleSubcategorySelect = (subcategoryId: string) => {
    setForm(prev => ({
      ...prev,
      category: subcategoryId,
    }));
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.youtube && !form.soundcloudUrl && !form.spotifyUrl && !form.instagramUrl) {
      setUrlError('Please paste a valid YouTube, Spotify, SoundCloud, or Instagram link.');
      return;
    }
    
    const categoryToSend = hasSubcategories ? form.category : selectedParent;
    if (!categoryToSend) {
      setError('Please select a category');
      return;
    }
    
    setSaving(true);
    try {
      const payload = { ...form, category: categoryToSend };
      // Clear unused media fields — keep only the selected platform's URL.
      payload.youtube = mediaType === 'youtube' ? form.youtube : '';
      payload.soundcloudUrl = mediaType === 'soundcloud' ? form.soundcloudUrl : '';
      payload.spotifyUrl = mediaType === 'spotify' ? form.spotifyUrl : '';
      payload.instagramUrl = mediaType === 'instagram' ? form.instagramUrl : '';
      const response = await axios.post(BASE_URL + '/api/showcase', payload);
      setAddOpen(false);
      setForm(emptyMusic);
      setSelectedParent('');
      setMediaType('youtube');
      setUrlInput('');
      setUrlError('');
      toast.success('Showcase added successfully!');
      adjustUsage('showcases', 1);

      if (response.data) {
        // Prepend new item and update order values for existing items
        setMusic(prev => [
          response.data,
          ...prev.map(item => ({ ...item, order: item.order + 1 }))
        ]);
      } else {
        fetchMusic();
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to add showcase';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item: Music) => {
    setEditId(item._id);
    const cat = categories.find(c => c._id === item.category);

    // Determine if it's a subcategory or top-level category
    if (cat?.parentCategory?._id) {
      setSelectedParent(cat.parentCategory._id);
    } else if (cat?._id) {
      setSelectedParent(cat._id);
    } else {
      setSelectedParent('');
    }

    // Detect media type from item
    setMediaType(item.instagramUrl ? 'instagram' : item.spotifyUrl ? 'spotify' : item.soundcloudUrl ? 'soundcloud' : 'youtube');
    setUrlInput(item.instagramUrl || item.spotifyUrl || item.soundcloudUrl || item.youtube || '');

    setForm({ ...item, soundcloudUrl: item.soundcloudUrl || '', spotifyUrl: item.spotifyUrl || '', instagramUrl: item.instagramUrl || '', thumbnail: item.thumbnail || (item.youtube ? `https://img.youtube.com/vi/${extractYouTubeVideoId(item.youtube)}/hqdefault.jpg` : ''), order: item.order });
    setEditOpen(true);
    setError('');
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.youtube && !form.soundcloudUrl && !form.spotifyUrl && !form.instagramUrl) {
      setUrlError('Please paste a valid YouTube, Spotify, SoundCloud, or Instagram link.');
      return;
    }
    
    const categoryToSend = hasSubcategories ? form.category : selectedParent;
    if (!categoryToSend) {
      setError('Please select a category');
      return;
    }
    
    setSaving(true);
    const currentEditId = editId;
    try {
      const payload = { ...form, category: categoryToSend };
      // Clear unused media fields — keep only the selected platform's URL.
      payload.youtube = mediaType === 'youtube' ? form.youtube : '';
      payload.soundcloudUrl = mediaType === 'soundcloud' ? form.soundcloudUrl : '';
      payload.spotifyUrl = mediaType === 'spotify' ? form.spotifyUrl : '';
      payload.instagramUrl = mediaType === 'instagram' ? form.instagramUrl : '';
      const response = await axios.put(`${BASE_URL}/api/showcase/${editId}`, payload);
      setEditOpen(false);
      setEditId(null);
      setForm(emptyMusic);
      setSelectedParent('');
      setMediaType('youtube');
      setUrlInput('');
      setUrlError('');
      toast.success('Showcase updated successfully!');

      if (response.data) {
        setMusic(prev => prev.map(item =>
          item._id === currentEditId ? { ...item, ...response.data } : item
        ));
      } else {
        fetchMusic();
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to update showcase';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const deletedItem = music.find(item => item._id === deleteId);
    // Optimistic removal
    setMusic(prev => prev.filter(item => item._id !== deleteId));
    setDeleteId(null);
    try {
      await axios.delete(`${BASE_URL}/api/showcase/${deleteId}`);
      toast.success('Showcase deleted successfully!');
      adjustUsage('showcases', -1);

    } catch (err: any) {
      // Revert on error
      if (deletedItem) {
        setMusic(prev => [...prev, deletedItem].sort((a, b) => a.order - b.order));
      }
      const errorMessage = err.response?.data?.message || 'Failed to delete showcase';
      toast.error(errorMessage);
    }
  };

  const handleToggleActive = async (id: string) => {
    try {
      setTogglingId(id);
      const response = await axios.patch(`${BASE_URL}/api/showcase/${id}/toggle-active`);
      setMusic(prev => prev.map(item => item._id === id ? response.data : item));
      toast.success(`Showcase ${response.data.isActive ? 'shown' : 'hidden'} successfully!`);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to toggle showcase status';
      toast.error(errorMessage);
    } finally {
      setTogglingId(null);
    }
  };

  const handleCategoryChange = async (id: string, categoryId: string) => {
    const item = music.find(m => m._id === id);
    if (!item || item.category === categoryId) return;

    // Optimistic update
    setMusic(prev => prev.map(m => m._id === id ? { ...m, category: categoryId } : m));
    try {
      await axios.put(`${BASE_URL}/api/showcase/${id}`, {
        category: categoryId,
        youtube: item.youtube || '',
        soundcloudUrl: item.soundcloudUrl || '',
        spotifyUrl: item.spotifyUrl || '',
        instagramUrl: item.instagramUrl || '',
      });
      toast.success('Category updated');
    } catch (err: any) {
      // Revert on failure
      setMusic(prev => prev.map(m => m._id === id ? { ...m, category: item.category } : m));
      toast.error(err.response?.data?.message || 'Failed to update category');
    }
  };

  const renderCustomThumbnailField = () => (
    <div className="space-y-1.5">
      <Label className="form-label">Custom Thumbnail (optional)</Label>
      <div className="flex items-center gap-2">
        <ImageUploader
          onImageUploaded={(url) => setForm(prev => ({ ...prev, thumbnail: url }))}
          aspectRatio={16 / 9}
          buttonLabel={form.thumbnail ? 'Replace thumbnail' : 'Upload thumbnail'}
        />
        {form.thumbnail && (
          <Button type="button" variant="outline" size="sm" onClick={() => setForm(prev => ({ ...prev, thumbnail: '' }))}>
            Remove
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Instagram's cover is saved automatically. Upload your own image to override it, or remove it to show the placeholder.
      </p>
    </div>
  );

  const renderCategoryInputs = () => (
    <div className="space-y-2">
      <Dropdown
        value={selectedParent}
        options={[
          { value: '', label: 'Select Category' },
          ...parentCategories.map(p => ({ value: p._id, label: p.name })),
        ]}
        onValueChange={(val) => {
          const parentId = val as string;
          if (!parentId) {
            setSelectedParent('');
            setForm(prev => ({ ...prev, category: '' }));
          } else {
            handleParentSelect(parentId);
          }
        }}
      />

      {hasSubcategories && (
        <Dropdown
          value={form.category}
          options={[
            { value: '', label: 'Select Subcategory' },
            ...subcategories.map(s => ({ value: s._id, label: s.name })),
          ]}
          onValueChange={(val) => handleSubcategorySelect(val as string)}
        />
      )}

      {!hasSubcategories && selectedParent && (
        <input type="hidden" name="category" value={selectedParent} />
      )}
    </div>
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = music.findIndex((item) => item._id === active.id);
      const newIndex = music.findIndex((item) => item._id === over.id);
      
      const newItems = arrayMove(music, oldIndex, newIndex);
      setMusic(newItems);
      setSortMode('custom');
      
      try {
        await axios.post(`${BASE_URL}/api/showcase/reorder`, { items: newItems });
        toast.success('Showcase order updated successfully!');
      } catch (err: any) {
        toast.error(err.response?.data?.message || 'Failed to update showcase order');
        // Revert the order if the API call fails
        fetchMusic();
      }
    }
  };

  // Sort like onboarding — sorting persists the new order to the backend so
  // the sequence stays consistent everywhere (onboarding, here, public site).
  const handleSortChange = async (mode: SortMode) => {
    setSortMode(mode);
    if (mode === 'custom') return;

    const items = [...music];
    if (mode === 'views') {
      items.sort((a, b) => (b.metadata?.views ?? 0) - (a.metadata?.views ?? 0));
    } else {
      items.sort((a, b) => {
        const dateA = a.metadata?.releaseDate || a.createdAt || '';
        const dateB = b.metadata?.releaseDate || b.createdAt || '';
        return new Date(dateB).getTime() - new Date(dateA).getTime();
      });
    }
    const reordered = items.map((item, index) => ({ ...item, order: index }));
    setMusic(reordered);

    try {
      await axios.post(`${BASE_URL}/api/showcase/reorder`, { items: reordered });
      toast.success('Showcase order updated successfully!');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to update showcase order');
      fetchMusic();
    }
  };

  // ── Super-admin showcase rebuild ──
  const fetchRebuildPreview = async (input?: string) => {
    setRebuildLoading(true);
    setRebuildError('');
    try {
      const res = await axios.post(`${BASE_URL}/api/showcase/rebuild-preview`, {
        youtubeInput: input || rebuildInput,
      });
      if (res.data.success) {
        setRebuildPreview(res.data.preview);
      } else {
        setRebuildError(res.data.message || 'Preview failed');
      }
    } catch (err: any) {
      setRebuildError(err.response?.data?.message || 'Failed to load preview');
    } finally {
      setRebuildLoading(false);
    }
  };

  const handleRebuildOpen = async () => {
    setRebuildOpen(true);
    setRebuildInput('');
    setRebuildPreview(null);
    setRebuildError('');
    setRebuildConfirming(false);
    await fetchRebuildPreview('');
  };

  const handleRebuildConfirm = async () => {
    if (!rebuildPreview) return;
    setRebuildConfirming(true);
    setRebuildError('');
    try {
      const res = await axios.post(`${BASE_URL}/api/showcase/rebuild-confirm`, {
        targetUserId: rebuildPreview.targetUserId,
        artistName: rebuildPreview.artistName,
        songs: rebuildPreview.songs || [],
        backfills: rebuildPreview.backfills || [],
      });
      if (res.data.success) {
        toast.success(`Showcase rebuilt: ${res.data.inserted} added, ${res.data.backfilled} backfilled`);
        setRebuildOpen(false);
        setRebuildPreview(null);
        fetchMusic();
      } else {
        setRebuildError(res.data.message || 'Rebuild failed');
      }
    } catch (err: any) {
      setRebuildError(err.response?.data?.message || 'Failed to confirm rebuild');
    } finally {
      setRebuildConfirming(false);
    }
  };

  return (
    <div>
      <GracePeriodBanner />
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="page-title">Showcase Management</h2>
          <PlanLimitBadge resourceType="showcases" />
        </div>
        <div className="flex items-center gap-2">
          {music.length > 1 && (
            <Dropdown
              value={sortMode}
              options={[
                { value: 'custom', label: 'Custom Order' },
                { value: 'views', label: 'Most Views' },
                { value: 'latest', label: 'Latest First' },
              ]}
              onValueChange={(val) => handleSortChange(val as SortMode)}
              triggerClassName="!w-auto"
              panelClassName="!min-w-40"
            />
          )}
          {isSuperAdmin && (
            <Button variant="outline" onClick={handleRebuildOpen}>
              <Wrench className="w-4 h-4 mr-2" />
              Rebuild
            </Button>
          )}
        <Dialog open={addOpen} onOpenChange={(open) => { setAddOpen(open); if (!open) { setUrlInput(''); setUrlError(''); setForm(emptyMusic); setSelectedParent(''); setMediaType('youtube'); } }}>
          <DialogTrigger asChild>
            <Button 
              onClick={(e) => {
                if (!canAdd('showcases')) {
                  e.preventDefault();
                  e.stopPropagation();
                  openUpgradeModal('showcases');
                } else {
                  setMediaType('youtube');
                  setUrlInput('');
                  setForm(emptyMusic);
                }
              }}
            >
              Add Showcase
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Showcase</DialogTitle>
              <DialogDescription>Add a new showcase item via YouTube, Spotify, SoundCloud, or Instagram</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAdd} className="space-y-5">
              {/* Single URL input — auto-detects YouTube, Spotify, or SoundCloud */}
              <div className="space-y-1.5">
                <Label htmlFor="urlInput" className="form-label">Link</Label>
                <div className="relative">
                  <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                  <Input
                    id="urlInput"
                    name="urlInput"
                    placeholder="Paste a YouTube, Spotify, SoundCloud, or Instagram link..."
                    value={urlInput}
                    onChange={handleUrlInputChange}
                    required
                    className="pl-10 pr-3.5"
                  />
                </div>
                {urlError && (
                  <p className="text-sm text-destructive">{urlError}</p>
                )}
                {!urlError && urlInput && (form.youtube || form.soundcloudUrl || form.spotifyUrl || form.instagramUrl) && (
                  <div className={`flex items-center gap-1.5 text-sm font-medium ${mediaType === 'youtube' ? 'text-[#FF0000]' : mediaType === 'soundcloud' ? 'text-[#FF5500]' : mediaType === 'instagram' ? 'text-[#E4405F]' : 'text-[#1DB954]'}`}>
                    {mediaType === 'instagram' ? (
                      <Instagram className="w-6 h-6 shrink-0" />
                    ) : mediaType === 'youtube' ? (
                      <svg viewBox="0 0 24 24" className="w-6 h-6 shrink-0" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                    ) : mediaType === 'soundcloud' ? (
                      
                      <svg viewBox="0 0 24 24" className="w-6 h-6 shrink-0" fill="currentColor"><path d="M1.175 12.225c-.015 0-.024.01-.024.023l-.272 2.308.272 2.316c0 .014.01.023.024.023.013 0 .023-.01.023-.023l.308-2.316-.308-2.308c0-.013-.01-.023-.023-.023zm.89-.899c-.02 0-.032.013-.032.03l-.311 3.206.31 3.234c0 .018.013.031.032.031.018 0 .031-.013.031-.03l.353-3.235-.353-3.206c0-.017-.013-.03-.031-.03zm.919-.31c-.023 0-.04.017-.04.039l-.295 3.516.295 3.544c0 .022.017.039.04.039.022 0 .04-.017.04-.039l.335-3.544-.335-3.516c0-.022-.018-.039-.04-.039zm.927-.164c-.027 0-.048.021-.048.047l-.277 3.68.277 3.705c0 .026.021.047.048.047.026 0 .047-.021.047-.047l.316-3.705-.316-3.68c0-.026-.021-.047-.047-.047zm.934-.11c-.03 0-.054.024-.054.054l-.26 3.79.26 3.811c0 .03.024.054.054.054.03 0 .054-.024.054-.054l.295-3.811-.295-3.79c0-.03-.024-.054-.054-.054zm.944-.082c-.033 0-.06.027-.06.061l-.244 3.872.244 3.89c0 .033.027.06.06.06.034 0 .06-.027.06-.06l.278-3.89-.278-3.872c0-.034-.026-.061-.06-.061zm.955-.055c-.037 0-.067.03-.067.067l-.228 3.927.228 3.942c0 .037.03.067.067.067.037 0 .067-.03.067-.067l.26-3.942-.26-3.927c0-.037-.03-.067-.067-.067zm.964-.04c-.04 0-.073.033-.073.073l-.212 3.967.212 3.978c0 .04.033.073.073.073.04 0 .073-.033.073-.073l.24-3.978-.24-3.967c0-.04-.033-.073-.073-.073zm.974-.027c-.044 0-.08.036-.08.08l-.195 3.994.195 4.002c0 .044.036.08.08.08.044 0 .08-.036.08-.08l.221-4.002-.221-3.994c0-.044-.036-.08-.08-.08zm.985-.014c-.047 0-.086.039-.086.086l-.179 4.008.179 4.015c0 .047.039.086.086.086.047 0 .086-.039.086-.086l.202-4.015-.202-4.008c0-.047-.039-.086-.086-.086zm.994-.003c-.05 0-.092.042-.092.092l-.162 4.011.162 4.017c0 .05.042.092.092.092.05 0 .092-.042.092-.092l.184-4.017-.184-4.011c0-.05-.042-.092-.092-.092zm1.003.01c-.054 0-.098.044-.098.098l-.146 4.001.146 4.006c0 .054.044.098.098.098.054 0 .098-.044.098-.098l.165-4.006-.165-4.001c0-.054-.044-.098-.098-.098zm1.012.023c-.057 0-.104.047-.104.104l-.13 3.978.13 3.981c0 .057.047.104.104.104.057 0 .104-.047.104-.104l.147-3.981-.147-3.978c0-.057-.047-.104-.104-.104zm1.021.037c-.06 0-.11.05-.11.11l-.113 3.941.113 3.943c0 .06.05.11.11.11.06 0 .11-.05.11-.11l.128-3.943-.128-3.941c0-.06-.05-.11-.11-.11zm1.03.05c-.063 0-.114.051-.114.114l-.097 3.891.097 3.892c0 .063.051.114.114.114.063 0 .114-.051.114-.114l.11-3.892-.11-3.891c0-.063-.051-.114-.114-.114zm1.039.063c-.066 0-.12.054-.12.12l-.08 3.828.08 3.829c0 .066.054.12.12.12.066 0 .12-.054.12-.12l.091-3.829-.091-3.828c0-.066-.054-.12-.12-.12zm1.048.077c-.069 0-.125.056-.125.125l-.064 3.751.064 3.751c0 .069.056.125.125.125.069 0 .125-.056.125-.125l.072-3.751-.072-3.751c0-.069-.056-.125-.125-.125zm1.057.09c-.072 0-.13.058-.13.13l-.047 3.661.047 3.66c0 .072.058.13.13.13.072 0 .13-.058.13-.13l.053-3.66-.053-3.661c0-.072-.058-.13-.13-.13zm2.186-.545c-.282 0-.548.06-.787.167-.163-1.853-1.72-3.295-3.617-3.295-.496 0-.967.104-1.39.291-.159.066-.201.133-.203.194v6.477c.002.063.051.113.114.118h5.883c.483 0 .875-.392.875-.875v-2.202c0-.483-.392-.875-.875-.875z"/></svg>
                    ) : (
                      <svg viewBox="0 0 24 24" className="w-6 h-6 shrink-0" fill="currentColor"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>
                    )}
                    Detected: {mediaType === 'youtube' ? 'YouTube' : mediaType === 'soundcloud' ? 'SoundCloud' : mediaType === 'instagram' ? 'Instagram' : 'Spotify'}
                  </div>
                )}
              </div>

              {/* Title — shown once a platform link is detected */}
              {(form.youtube || form.soundcloudUrl || form.spotifyUrl || form.instagramUrl) && (
                <div className="space-y-1.5">
                  <Label htmlFor="title" className="form-label">Title</Label>
                  {isFetching ? (
                    <div className="h-9 bg-muted rounded-lg animate-pulse" />
                  ) : (
                    <Input
                      id="title"
                      name="title"
                      placeholder="Enter a title for this track"
                      value={form.title}
                      onChange={handleFormChange}
                      required
                    />
                  )}
                </div>
              )}

              {/* Category */}
              <div className="space-y-1.5">
                <Label className="form-label">Category</Label>
                {renderCategoryInputs()}
                {error && error.includes('category') && (
                  <p className="text-sm text-destructive">{error}</p>
                )}
              </div>

              {/* Inline video preview (YouTube only) */}
              {mediaType === 'youtube' && extractYouTubeVideoId(form.youtube) && form.thumbnail && (
                <div className="bg-secondary rounded-lg border border-border overflow-hidden max-w-xs">
                  <div className="relative aspect-video bg-muted">
                    <img
                      src={form.thumbnail}
                      alt="Video thumbnail"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const vid = extractYouTubeVideoId(form.youtube);
                        if (vid) (e.target as HTMLImageElement).src = `https://img.youtube.com/vi/${vid}/hqdefault.jpg`;
                      }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-10 h-10 rounded-full bg-black/60 flex items-center justify-center">
                        <Play className="w-4 h-4 text-white ml-0.5" fill="white" />
                      </div>
                    </div>
                  </div>
                  <div className="p-2">
                    {isFetching ? (
                      <div className="h-4 bg-muted rounded animate-pulse w-36" />
                    ) : (
                      <p className="text-xs font-medium text-foreground truncate">
                        {form.title || 'Untitled Video'}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* SoundCloud preview */}
              {mediaType === 'soundcloud' && form.soundcloudUrl && form.thumbnail && (
                <div className="bg-secondary rounded-lg border border-border overflow-hidden max-w-xs">
                  <div className="relative aspect-square bg-muted max-h-40">
                    <img
                      src={form.thumbnail}
                      alt="Track artwork"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-10 h-10 rounded-full bg-black/60 flex items-center justify-center">
                        <Headphones className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  </div>
                  <div className="p-2">
                    {isFetching ? (
                      <div className="h-4 bg-muted rounded animate-pulse w-36" />
                    ) : (
                      <p className="text-xs font-medium text-foreground truncate">
                        {form.title || 'Untitled Track'}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Spotify preview */}
              {mediaType === 'spotify' && extractSpotifyTrackId(form.spotifyUrl) && (
                <div className="bg-secondary rounded-lg border border-border overflow-hidden max-w-xs">
                  {form.thumbnail && (
                    <div className="relative aspect-square bg-muted max-h-40">
                      <img
                        src={form.thumbnail}
                        alt="Album artwork"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-10 h-10 rounded-full bg-black/60 flex items-center justify-center">
                          <svg viewBox="0 0 24 24" className="w-5 h-5 text-[#1DB954]" fill="currentColor">
                            <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="p-2">
                    {isFetching ? (
                      <div className="h-4 bg-muted rounded animate-pulse w-36" />
                    ) : (
                      <p className="text-xs font-medium text-foreground truncate">
                        {form.title || 'Untitled Track'}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Instagram preview */}
              {mediaType === 'instagram' && form.instagramUrl && (
                <div className="bg-secondary rounded-lg border border-border overflow-hidden max-w-xs">
                  {form.thumbnail ? (
                    <div className="relative aspect-square bg-muted max-h-40">
                      <img src={form.thumbnail} alt="Post thumbnail" className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="relative aspect-square bg-muted max-h-40 flex items-center justify-center">
                      <Instagram className="w-8 h-8 text-[#E4405F]" />
                    </div>
                  )}
                  <div className="p-2">
                    {isFetching ? (
                      <div className="h-4 bg-muted rounded animate-pulse w-36" />
                    ) : (
                      <p className="text-xs font-medium text-foreground truncate">
                        {form.title || 'Untitled'}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {mediaType === 'instagram' && form.instagramUrl && renderCustomThumbnailField()}
              {error && !error.includes('category') && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" loading={saving} loadingText="Adding...">{mediaType === 'youtube' ? 'Add Video' : mediaType === 'soundcloud' ? 'Add Track' : mediaType === 'instagram' ? 'Add Media' : 'Add Track'}</Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <SectionTextSettingsCard sectionKey="showcase" />

      <div>
        {loading ? (
          <PageLoader label="Loading showcase..." />
        ) : music.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <Music className="w-full h-full" />
            </div>
            <h3 className="empty-state-title">No showcase items found</h3>
            <p className="empty-state-description">Add your first music showcase item to get started</p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-10">Order</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Thumbnail</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Website</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="text-left">
                <SortableContext items={paginatedMusic.map(item => item._id)} strategy={verticalListSortingStrategy}>
                  {paginatedMusic.map((item, idx) => (
                    <SortableTableRow
                      key={item._id}
                      item={item}
                      index={startIndex + idx}
                      categories={categories}
                      onEdit={handleEdit}
                      onDelete={setDeleteId}
                      onToggleActive={handleToggleActive}
                      onCategoryChange={handleCategoryChange}
                      togglingId={togglingId}
                    />
                  ))}
                </SortableContext>
              </TableBody>
            </Table>

            {/* Pagination Controls */}
            <Pagination
              {...paginationProps}
              itemLabel="items"
            />
          </DndContext>
        )}
      </div>

      <Dialog open={editOpen} onOpenChange={(open) => { setEditOpen(open); if (!open) { setUrlInput(''); setUrlError(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Showcase</DialogTitle>
            <DialogDescription>Update the showcase item details</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-5">
              {/* Single URL input — auto-detects YouTube, Spotify, or SoundCloud */}
              <div className="space-y-1.5">
                <Label htmlFor="edit-urlInput" className="form-label">Link</Label>
                <div className="relative">
                  <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                  <Input
                    id="edit-urlInput"
                    name="urlInput"
                    placeholder="Paste a YouTube, Spotify, SoundCloud, or Instagram link..."
                    value={urlInput}
                    onChange={handleUrlInputChange}
                    required
                    className="pl-10 pr-3.5"
                  />
                </div>
                {urlError && (
                  <p className="text-sm text-destructive">{urlError}</p>
                )}
                {!urlError && urlInput && (
                  <div className={`flex items-center gap-1.5 text-xs font-medium ${mediaType === 'youtube' ? 'text-[#FF0000]' : mediaType === 'soundcloud' ? 'text-[#FF5500]' : mediaType === 'instagram' ? 'text-[#E4405F]' : 'text-[#1DB954]'}`}>
                    {mediaType === 'instagram' ? (
                      <Instagram className="w-3 h-3 shrink-0" />
                    ) : mediaType === 'youtube' ? (
                      <svg viewBox="0 0 24 24" className="w-3 h-3 shrink-0" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                    ) : mediaType === 'soundcloud' ? (
                      <svg viewBox="0 0 24 24" className="w-3 h-3 shrink-0" fill="currentColor"><path d="M1.175 12.225c-.015 0-.024.01-.024.023l-.272 2.308.272 2.316c0 .014.01.023.024.023.013 0 .023-.01.023-.023l.308-2.316-.308-2.308c0-.013-.01-.023-.023-.023zm.89-.899c-.02 0-.032.013-.032.03l-.311 3.206.31 3.234c0 .018.013.031.032.031.018 0 .031-.013.031-.03l.353-3.235-.353-3.206c0-.017-.013-.03-.031-.03zm.919-.31c-.023 0-.04.017-.04.039l-.295 3.516.295 3.544c0 .022.017.039.04.039.022 0 .04-.017.04-.039l.335-3.544-.335-3.516c0-.022-.018-.039-.04-.039zm.927-.164c-.027 0-.048.021-.048.047l-.277 3.68.277 3.705c0 .026.021.047.048.047.026 0 .047-.021.047-.047l.316-3.705-.316-3.68c0-.026-.021-.047-.047-.047zm.934-.11c-.03 0-.054.024-.054.054l-.26 3.79.26 3.811c0 .03.024.054.054.054.03 0 .054-.024.054-.054l.295-3.811-.295-3.79c0-.03-.024-.054-.054-.054zm.944-.082c-.033 0-.06.027-.06.061l-.244 3.872.244 3.89c0 .033.027.06.06.06.034 0 .06-.027.06-.06l.278-3.89-.278-3.872c0-.034-.026-.061-.06-.061zm.955-.055c-.037 0-.067.03-.067.067l-.228 3.927.228 3.942c0 .037.03.067.067.067.037 0 .067-.03.067-.067l.26-3.942-.26-3.927c0-.037-.03-.067-.067-.067zm.964-.04c-.04 0-.073.033-.073.073l-.212 3.967.212 3.978c0 .04.033.073.073.073.04 0 .073-.033.073-.073l.24-3.978-.24-3.967c0-.04-.033-.073-.073-.073zm.974-.027c-.044 0-.08.036-.08.08l-.195 3.994.195 4.002c0 .044.036.08.08.08.044 0 .08-.036.08-.08l.221-4.002-.221-3.994c0-.044-.036-.08-.08-.08zm.985-.014c-.047 0-.086.039-.086.086l-.179 4.008.179 4.015c0 .047.039.086.086.086.047 0 .086-.039.086-.086l.202-4.015-.202-4.008c0-.047-.039-.086-.086-.086zm.994-.003c-.05 0-.092.042-.092.092l-.162 4.011.162 4.017c0 .05.042.092.092.092.05 0 .092-.042.092-.092l.184-4.017-.184-4.011c0-.05-.042-.092-.092-.092zm1.003.01c-.054 0-.098.044-.098.098l-.146 4.001.146 4.006c0 .054.044.098.098.098.054 0 .098-.044.098-.098l.165-4.006-.165-4.001c0-.054-.044-.098-.098-.098zm1.012.023c-.057 0-.104.047-.104.104l-.13 3.978.13 3.981c0 .057.047.104.104.104.057 0 .104-.047.104-.104l.147-3.981-.147-3.978c0-.057-.047-.104-.104-.104zm1.021.037c-.06 0-.11.05-.11.11l-.113 3.941.113 3.943c0 .06.05.11.11.11.06 0 .11-.05.11-.11l.128-3.943-.128-3.941c0-.06-.05-.11-.11-.11zm1.03.05c-.063 0-.114.051-.114.114l-.097 3.891.097 3.892c0 .063.051.114.114.114.063 0 .114-.051.114-.114l.11-3.892-.11-3.891c0-.063-.051-.114-.114-.114zm1.039.063c-.066 0-.12.054-.12.12l-.08 3.828.08 3.829c0 .066.054.12.12.12.066 0 .12-.054.12-.12l.091-3.829-.091-3.828c0-.066-.054-.12-.12-.12zm1.048.077c-.069 0-.125.056-.125.125l-.064 3.751.064 3.751c0 .069.056.125.125.125.069 0 .125-.056.125-.125l.072-3.751-.072-3.751c0-.069-.056-.125-.125-.125zm1.057.09c-.072 0-.13.058-.13.13l-.047 3.661.047 3.66c0 .072.058.13.13.13.072 0 .13-.058.13-.13l.053-3.66-.053-3.661c0-.072-.058-.13-.13-.13zm2.186-.545c-.282 0-.548.06-.787.167-.163-1.853-1.72-3.295-3.617-3.295-.496 0-.967.104-1.39.291-.159.066-.201.133-.203.194v6.477c.002.063.051.113.114.118h5.883c.483 0 .875-.392.875-.875v-2.202c0-.483-.392-.875-.875-.875z"/></svg>
                    ) : (
                      <svg viewBox="0 0 24 24" className="w-3 h-3 shrink-0" fill="currentColor"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>
                    )}
                    {mediaType === 'youtube' ? 'YouTube' : mediaType === 'soundcloud' ? 'SoundCloud' : mediaType === 'instagram' ? 'Instagram' : 'Spotify'}
                  </div>
                )}
              </div>

              <div className="form-group">
                <Label htmlFor="edit-title" className="form-label">Title</Label>
                <Input
                  id="edit-title"
                  name="title"
                  placeholder="Enter title"
                  value={form.title}
                  onChange={handleFormChange}
                  required
                />
              </div>

              {/* Preview */}
              {mediaType === 'youtube' && extractYouTubeVideoId(form.youtube) && (
                <div className="bg-secondary rounded-lg border border-border overflow-hidden max-w-xs">
                  <div className="relative aspect-video bg-muted">
                    <img
                      src={form.thumbnail || `https://img.youtube.com/vi/${extractYouTubeVideoId(form.youtube)}/hqdefault.jpg`}
                      alt="Video thumbnail"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const vid = extractYouTubeVideoId(form.youtube);
                        if (vid) (e.target as HTMLImageElement).src = `https://img.youtube.com/vi/${vid}/hqdefault.jpg`;
                      }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-10 h-10 rounded-full bg-black/60 flex items-center justify-center">
                        <Play className="w-4 h-4 text-white ml-0.5" fill="white" />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {mediaType === 'spotify' && form.thumbnail && (
                <div className="bg-secondary rounded-lg border border-border overflow-hidden max-w-xs">
                  <div className="relative aspect-square bg-muted max-h-40">
                    <img
                      src={form.thumbnail}
                      alt="Album artwork"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-10 h-10 rounded-full bg-black/60 flex items-center justify-center">
                        <svg viewBox="0 0 24 24" className="w-5 h-5 text-[#1DB954]" fill="currentColor">
                          <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="form-group">
                <Label className="form-label">Category</Label>
                {renderCategoryInputs()}
                {error && error.includes('category') && (
                  <p className="text-sm text-destructive">{error}</p>
                )}
              </div>
              {/* Instagram preview */}
              {mediaType === 'instagram' && form.instagramUrl && (
                <div className="bg-secondary rounded-lg border border-border overflow-hidden max-w-xs">
                  {form.thumbnail ? (
                    <div className="relative aspect-square bg-muted max-h-40">
                      <img src={form.thumbnail} alt="Post thumbnail" className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="relative aspect-square bg-muted max-h-40 flex items-center justify-center">
                      <Instagram className="w-8 h-8 text-[#E4405F]" />
                    </div>
                  )}
                </div>
              )}
              {mediaType === 'instagram' && form.instagramUrl && renderCustomThumbnailField()}
            {error && !error.includes('category') && <div className="text-danger">{error}</div>}
            <Button type="submit" loading={saving} loadingText="Saving...">Save</Button>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteId}
        onOpenChange={(open) => !open && setDeleteId(null)}
        title="Delete Showcase"
        description="Are you sure you want to delete this showcase item? This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={handleDelete}
      />

      {/* Super-admin rebuild dialog */}
      <Dialog open={rebuildOpen} onOpenChange={(open) => { setRebuildOpen(open); if (!open) { setRebuildInput(''); setRebuildPreview(null); setRebuildError(''); setRebuildConfirming(false); } }}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5" />
              Rebuild Showcase
            </DialogTitle>
            <DialogDescription>
              Re-run YouTube discovery for this artist. Manual showcase items are preserved.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-end gap-2">
              <div className="space-y-1.5 flex-1 min-w-0">
                <Label htmlFor="rebuild-input" className="form-label">YouTube channel (optional)</Label>
                <Input
                  id="rebuild-input"
                  placeholder="Paste YouTube channel link or @handle, or leave empty"
                  value={rebuildInput}
                  onChange={(e) => setRebuildInput(e.target.value)}
                  disabled={rebuildLoading || rebuildConfirming}
                />
              </div>
              <Button
                variant="outline"
                onClick={() => fetchRebuildPreview()}
                disabled={rebuildLoading || rebuildConfirming}
                loading={rebuildLoading}
                loadingText="Discovering..."
                className="shrink-0"
              >
                Discover Songs
              </Button>
            </div>
            {rebuildPreview?.artistName && (
              <p className="text-xs text-muted-foreground">
                Artist source: <span className="font-medium text-foreground">{rebuildPreview.artistName}</span>
              </p>
            )}

            {rebuildError && (
              <p className="text-sm text-destructive">{rebuildError}</p>
            )}

            {rebuildPreview && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { label: 'Plan', value: rebuildPreview.plan, capitalize: true },
                    { label: 'Current', value: rebuildPreview.currentShowcases },
                    { label: 'Manual kept', value: rebuildPreview.preservedManualCount, accent: 'text-green-600' },
                    { label: 'Auto-imported', value: rebuildPreview.autoImportedShowcases },
                    { label: 'Discovered', value: rebuildPreview.discoveredSongs },
                    { label: 'Will add', value: rebuildPreview.willAddCount, accent: 'text-blue-600' },
                    { label: 'Will backfill', value: rebuildPreview.backfillCandidatesCount },
                    { label: 'Limit', value: rebuildPreview.showcaseLimit === -1 ? 'Unlimited' : `${rebuildPreview.showcaseLimit} (bypassed)` },
                  ].map((stat) => (
                    <div key={stat.label} className="bg-muted/50 rounded-lg px-3 py-2">
                      <p className="text-[11px] text-muted-foreground">{stat.label}</p>
                      <p className={`text-sm font-semibold ${stat.accent || ''} ${stat.capitalize ? 'capitalize' : ''}`}>{stat.value}</p>
                    </div>
                  ))}
                </div>

                {(rebuildPreview.willAdd?.length > 0 || rebuildPreview.backfillCandidates?.length > 0) && (
                  <div className="grid sm:grid-cols-2 gap-3">
                    {rebuildPreview.willAdd?.length > 0 && (
                      <div className="space-y-1.5">
                        <p className="text-xs font-medium text-foreground">Will add ({rebuildPreview.willAdd.length})</p>
                        <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
                          {rebuildPreview.willAdd.map((s: any, idx: number) => (
                            <div key={s.videoId || idx} className="flex items-center gap-2 text-xs bg-background/60 rounded px-2 py-1.5">
                              {s.videoId && (
                                <img
                                  src={`https://img.youtube.com/vi/${s.videoId}/default.jpg`}
                                  alt=""
                                  className="w-12 h-7 object-cover rounded shrink-0"
                                />
                              )}
                              <span className="truncate flex-1 min-w-0">{s.title}</span>
                              {s.views != null && <span className="text-muted-foreground shrink-0">{Number(s.views).toLocaleString()}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {rebuildPreview.backfillCandidates?.length > 0 && (
                      <div className="space-y-1.5">
                        <p className="text-xs font-medium text-foreground">Will backfill ({rebuildPreview.backfillCandidates.length})</p>
                        <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
                          {rebuildPreview.backfillCandidates.map((b: any) => (
                            <div key={b.id} className="flex items-center gap-2 text-xs bg-background/60 rounded px-2 py-1.5">
                              {b.videoId && (
                                <img
                                  src={`https://img.youtube.com/vi/${b.videoId}/default.jpg`}
                                  alt=""
                                  className="w-12 h-7 object-cover rounded shrink-0"
                                />
                              )}
                              <span className="truncate flex-1 min-w-0">{b.oldTitle}</span>
                              <span className="text-muted-foreground shrink-0">metadata</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-2 pt-1">
                  <Button
                    variant="outline"
                    onClick={() => setRebuildOpen(false)}
                    disabled={rebuildLoading || rebuildConfirming}
                    className="flex-1"
                  >
                    Discard
                  </Button>
                  <Button
                    onClick={handleRebuildConfirm}
                    disabled={rebuildLoading || rebuildConfirming}
                    loading={rebuildConfirming}
                    loadingText="Applying..."
                    className="flex-1"
                  >
                    Confirm Rebuild
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShowcaseManager;
